import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    CCard,
    CCardBody,
    CCardHeader,
    CRow,
    CCol,
    CAlert,
} from '@coreui/react';

import { apiClient } from 'services/Api';
import DataTableComponent from 'components/DataTableComponent';
import ToastNotification from 'components/globals/ToastNotification';
import { ROLE_LABELS, formatScore } from './questions';

const MyPerformanceResults = ({ selectedYear }) => {
    const [results, setResults] = useState([]);
    const [summary, setSummary] = useState(null);
    const [toast, setToast] = useState(null);

    const fetchMyResults = useCallback(
        async (year = selectedYear) => {
            try {
                const response = await apiClient.get(`/performance-assessments/my/?year=${year}`);

                const payload = response.data || {};
                const rows = Array.isArray(payload.rows) ? payload.rows : [];
                setResults(rows);

                setSummary(payload.summary || null);
            } catch (error) {
                console.error('Error fetching my performance results:', error);
                setToast({
                    title: 'Avaliação de Desempenho',
                    message: 'Erro ao carregar resultados das suas avaliações.',
                    type: 'error',
                });
                setResults([]);
                setSummary(null);
            }
        },
        [selectedYear]
    );

    useEffect(() => {
        fetchMyResults(selectedYear);
    }, [selectedYear, fetchMyResults]);

    const columns = useMemo(
        () => [
            {
                name: 'Departamento',
                selector: (row) => row.department_name,
                sortable: true,
            },
            {
                name: 'Peso',
                selector: (row) => row.department_weight,
                sortable: true,
                cell: (row) => `${row.department_weight ?? 0}%`,
                maxWidth: '90px',
            },
            {
                name: 'Função',
                selector: (row) => ROLE_LABELS[row.role] || row.role,
                sortable: true,
            },
            {
                name: 'Organizacionais',
                selector: (row) => row.c1,
                sortable: true,
                cell: (row) => formatScore(row.c1),
            },
            {
                name: 'Liderança',
                selector: (row) => row.c2,
                sortable: true,
                cell: (row) => formatScore(row.c2),
            },
            {
                name: 'Comportamentais',
                selector: (row) => row.c3,
                sortable: true,
                cell: (row) => formatScore(row.c3),
            },
            {
                name: 'Competencias Técnicas',
                selector: (row) => row.c4,
                sortable: true,
                cell: (row) => formatScore(row.c4),
            },
            {
                name: 'Final',
                selector: (row) => row.final_score,
                sortable: true,
                cell: (row) => formatScore(row.final_score),
                maxWidth: '110px',
            },
        ],
        []
    );

    return (
        <>
            <ToastNotification toast={toast} setToast={setToast} />

            <CCard className="mb-4">
                <CCardHeader>
                    <h4 className="mb-0">As suas Avaliações de Desempenho (médias por departamento/ano)</h4>
                </CCardHeader>

                <CCardBody>
                    {/* Summary section */}
                    {summary && (
                        <CRow className="g-3 mb-3">
                            <CCol md={4}>
                                <CCard className="h-100">
                                    <CCardBody>
                                        <div className="text-muted small">Média ponderada (final)</div>
                                        <div className="fs-4 fw-semibold">
                                            {summary.weighted_average_final_score == null
                                                ? '—'
                                                : formatScore(summary.weighted_average_final_score)}
                                        </div>
                                    </CCardBody>
                                </CCard>
                            </CCol>

                            <CCol md={4}>
                                <CCard className="h-100">
                                    <CCardBody>
                                        <div className="text-muted small">Objetivos funcionais</div>
                                        <div className="fs-4 fw-semibold">
                                            {summary.functionals == null ? '—' : summary.functionals}
                                        </div>
                                    </CCardBody>
                                </CCard>
                            </CCol>

                            <CCol md={4}>
                                <CCard className="h-100">
                                    <CCardBody>
                                        <div className="text-muted small">Avaliação final</div>
                                        <div className="fs-4 fw-semibold">
                                            {summary.final_score_with_functionals == null
                                                ? '—'
                                                : formatScore(summary.final_score_with_functionals)}
                                        </div>
                                    </CCardBody>
                                </CCard>
                            </CCol>
                        </CRow>
                    )}

                    <DataTableComponent columns={columns} data={results} />

                    <p className="mt-3 text-muted">
                        Só são apresentados resultados de avaliações concluídas.
                    </p>

                    {results.length > 0 && (
                        <CAlert color="info" className="mt-3 mb-0">
                            Nota: a “Média ponderada (final)” é o somatório das notas finais por departamento ponderadas pelo peso.
                        </CAlert>
                    )}
                </CCardBody>
            </CCard>
        </>
    );
};

export default MyPerformanceResults;
